import React, { useState, useRef } from 'react';
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import { CircularProgress } from "@mui/material";

import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import Messages from "../../../constants/Messages";
import { CheckIcon, XIcon } from '../../../constants/Icons';

const PayInModal = ({ isOpen, onClose,loading, isSelectedData, onMainModalReopen, onDataUpdate, onTransactionUpdate }) => {
    const [amount, setAmount] = useState("");
    const [description, setDescription] = useState("");
    const [paymentmode, setPaymentMode] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isBtnLoading, setBtnLoading] = useState(false);
    const amountRef = useRef(null);
    const inputRef = useRef(null);
    const descriptionRef = useRef(null);
    const buttonRef = useRef(null)

    const { checkNetwork } = useNetwork();
    const showAlert = useShowAlert();

    if (!isOpen) return null;

    const handleClose = () => {
        setAmount("");
        setDescription("");
        setPaymentMode("");
        setErrorMessage("");
        onClose();
        onMainModalReopen();
    };

    const onAdd = (event) => {
        event.preventDefault();
        if (!validateForm()) return;
        onTransactionUpdate(isSelectedData.customerid,amount,description,'PAYIN',paymentmode,handleClose);
        // handleClose();
    };

    const validateForm = () => {
        setErrorMessage("");

        if (!amount) {
            setErrorMessage("Amount cannot be empty.");
            amountRef?.current?.focus();
            return false;
        }
        const parsedValue = parseFloat(amount);
        if (isNaN(parsedValue) || parsedValue <= 0) {
            setErrorMessage("Enter a valid numeric amount greater than 0.");
            amountRef?.current?.focus();
            return false;
        }
        const decimalPart = amount.split(".")[1];
        if (decimalPart && decimalPart.length > 2) {
            setErrorMessage("Amount cannot have more than two decimal places (e.g., 22.22 or 90).");
            amountRef?.current?.focus();
            return false;
        }

        const validPaymentModes = ["CASH", "CARD", "UPI"];
        if (!paymentmode) {
            setErrorMessage("Payment mode cannot be empty.");
            inputRef?.current?.focus();
            return false;
        }
        if (!validPaymentModes.includes(paymentmode)) {
            setErrorMessage("Invalid payment mode selected.");
            inputRef?.current?.focus();
            return false;
        }

        if (description) {
            if (description.length > 50000) {
                setErrorMessage("Description cannot exceed 50000 characters.");
                descriptionRef?.current?.focus();
                return false;
            }
            if (!/^[a-zA-Z0-9\s.,-]*$/.test(description)) {
                setErrorMessage("Description can only contain letters, numbers, spaces, commas, periods, and hyphens.");
                descriptionRef?.current?.focus();
                return false;
            }
        }

        return true;
    };

    const handleInputChange = (value) => {
        let inputValue = value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
        const [integerPart, decimalPart] = inputValue.split(".");

        if (decimalPart && decimalPart.length > 2) {
            inputValue = `${integerPart}.${decimalPart.slice(0, 2)}`;
        }

        if (inputValue.match(/^0\d/)) {
            inputValue = inputValue.replace(/^0+/, "") || "0";
        }

        const parsedValue = parseFloat(inputValue) || 0;
        if (!inputValue || parsedValue <= 0) {
            setErrorMessage("Enter a valid amount greater than 0.");
            setAmount(inputValue || "");
        } else {
            setErrorMessage("");
            setAmount(inputValue);
        }
    };

    const handlePayIn = async () => {
        setBtnLoading(true);
        try {
            if (!checkNetwork()) return;
            const formattedAmount = parseFloat(amount).toFixed(2);
            const data = {
                customerid: isSelectedData._id,
                amount: CurrencyConverter.toSubunit(formattedAmount, 'INR'),
                description: description,
                paymode: "PAYIN",
                paymentmode: paymentmode,
            };
            await AxiosInstance.post(`module/paylater`, data);
            showAlert("success", Messages.SUCCESS.PAYLATER_ADDED);
            onDataUpdate();
            onTransactionUpdate();
            handleClose();
        } catch (error) {
            handleError(error, showAlert);
            return false;
        } finally {
            setBtnLoading(false);
        }
    };

    const handleEnterFocus = (e, nextRef) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if ((e.target === amountRef.current && !amount.trim())|| (e.target === inputRef.current && !paymentmode.trim())) {
            if (!validateForm()) return; 
          }

          if(e.target === descriptionRef.current){
            onAdd(e);
           }
          nextRef?.current?.focus();
        }
      };

    return (
        <div>
            <div className="modal-backdrop show"></div>
            <div
                className="modal show"
                tabIndex="-1"
                aria-modal="true"
                role="dialog"
                style={{ display: "block" }}
            >
                <div className="modal-dialog modal-dialog-centered modal-sm">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h6 className="modal-title">Pay In</h6>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <div className="card w-100 radius-10 mb-0 shadow-none">
                                <div className="card-body p-0">
                                    <form className="row g-3">
                                        <div className="col-md-12">
                                            <label className="form-label">
                                                Amount <small className="text-danger">*</small>
                                            </label>
                                            <input
                                                type="text"
                                                autoComplete="off"
                                                className="form-control"
                                                ref={amountRef}
                                                value={amount}
                                                onKeyDown={(e)=>handleEnterFocus(e,inputRef)}
                                                onChange={(e) => handleInputChange(e.target.value)}
                                                autoFocus
                                            />
                                            {errorMessage && errorMessage.includes("Amount") && (
                                                <small className="text-danger">{errorMessage}</small>
                                            )}
                                        </div>
                                        <div className="col-md-12">
                                            <label htmlFor="paymentmode" className="form-label">
                                                Payment Mode <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                name="paymentmode"
                                                className="form-select"
                                                ref={inputRef}
                                                value={paymentmode}
                                                onKeyDown={(e)=>handleEnterFocus(e,descriptionRef)}
                                                onChange={(e) => {setErrorMessage("");setPaymentMode(e.target.value)}}
                                            >
                                                <option value="">Select</option>
                                                <option value="CASH">Cash</option>
                                                <option value="CARD">Card</option>
                                                <option value="UPI">Online / UPI</option>
                                            </select>
                                            {errorMessage && errorMessage.includes("Payment mode") && (
                                                <small className="text-danger">{errorMessage}</small>
                                            )}
                                        </div>
                                        <div className="col-md-12">
                                            <label htmlFor="description" className="form-label">Description</label>
                                            <textarea
                                                ref={descriptionRef}
                                                type="text"
                                                autoComplete="off"
                                                maxLength="5000"
                                                className="form-control"
                                                value={description}
                                                onKeyDown={(e)=>handleEnterFocus(e,buttonRef)}
                                                onChange={(e) => setDescription(e.target.value)}
                                            ></textarea>
                                            {errorMessage && errorMessage.includes("Description") && (
                                                <small className="text-danger">{errorMessage}</small>
                                            )}
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer d-block">
                            <button
                                onClick={handleClose}
                                type="button"
                                data-bs-dismiss="modal"
                                className="btn btn-outline-secondary text-left"
                            >
                                <XIcon/> Close
                            </button>

                            {loading||isBtnLoading ? (
                                <button className="btn btn-success pb-0 text-right" disabled>
                                    <CircularProgress size={22} className="text-white" />
                                </button>
                            ) : (
                                <button
                                    ref={buttonRef}
                                    type="button"
                                    className="btn btn-success text-right"
                                    onClick={onAdd}
                                >
                                    <CheckIcon/> Pay In
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PayInModal;